
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MediaUploader } from "@/components/post/MediaUploader";
import { Loader2, Send } from "lucide-react";

interface CreateGroupPostProps {
  groupId: string;
  onPostCreated: () => void;
}

export const CreateGroupPost = ({ groupId, onPostCreated }: CreateGroupPostProps) => {
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleMediaUpload = (preview: string | null, type: "image" | "video" | null) => {
    if (preview) {
      setMediaUrl(preview);
      setMediaType(type);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to create posts",
          variant: "destructive",
        });
        return;
      }

      // First create the post with category explicitly set to 'group'
      const { data: post, error: postError } = await supabase
        .from('posts')
        .insert({
          user_id: user.id,
          content,
          image_url: mediaType === "image" ? mediaUrl : null,
          video_url: mediaType === "video" ? mediaUrl : null,
          category: 'group', // Use 'group' category for group posts
          is_private: false,
        })
        .select()
        .single();

      if (postError) throw postError;

      // Check if user is a member of the group first
      const { data: membership, error: membershipError } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', groupId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (membershipError) {
        console.error('Membership check error:', membershipError);
        toast({
          title: "Error checking membership",
          description: membershipError.message,
          variant: "destructive",
        });
        return;
      }

      if (!membership) {
        toast({
          title: "Access denied",
          description: "You must be a member of this group to post",
          variant: "destructive",
        });
        return;
      }

      // Then link it to the group with status set to approved for now
      const { error: groupPostError } = await supabase
        .from('group_posts')
        .insert({
          group_id: groupId,
          post_id: post.id,
          status: 'approved',
          category: 'discussion',
          tags: []
        });

      if (groupPostError) throw groupPostError;

      toast({
        title: "Post created",
        description: "Your post has been shared successfully.",
      });

      // Reset form
      setContent("");
      setMediaUrl(null);
      setMediaType(null);
      
      // Notify parent component that post was created
      onPostCreated();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create post",
        variant: "destructive",
      });
      console.error("Post creation error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-900/40 dark:to-purple-900/40 p-6 rounded-3xl shadow-md">
      <div className="relative">
        <Textarea
          placeholder="What do you want to share with the group?"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="min-h-[100px] bg-white/90 backdrop-blur-sm border-0 focus-visible:ring-pink-400 rounded-2xl resize-none text-base"
        />
      </div>
      
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        <div className="w-full sm:w-auto">
          <MediaUploader
            onMediaUpload={handleMediaUpload}
          />
        </div>
        <Button 
          type="submit" 
          disabled={!content.trim() || isLoading} 
          className="w-full sm:flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full px-4 py-2.5 min-h-[44px]"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span className="hidden xs:inline">Posting...</span>
              <span className="xs:hidden">...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              <span className="hidden xs:inline">Share with Group</span>
              <span className="xs:hidden">Share</span>
            </>
          )}
        </Button>
      </div>
      
      {mediaUrl && (
        <div className="relative rounded-2xl overflow-hidden w-full max-h-[200px] border-2 border-white shadow-md">
          {mediaType === "image" ? (
            <img src={mediaUrl} alt="Preview" className="object-cover w-full h-full" />
          ) : mediaType === "video" ? (
            <video src={mediaUrl} controls className="w-full h-full">
              <track kind="captions" />
            </video>
          ) : null}
        </div>
      )}
    </form>
  );
};
